import { isValidElement, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { manualImages, slugifyHeading } from './manual';

/** Recovers the plain text of a heading so it can be turned into an anchor id. */
export function toText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (isValidElement(node)) return toText((node.props as { children?: ReactNode }).children);
  return '';
}

/**
 * Resolves a relative `.md` link to a help route.
 *
 * Links in the markdown are written the way they work on disk and on GitHub —
 * "02-build-a-breakdown.md" from a sibling, "../workflows/04-run-timesheets.md"
 * from another folder — so they have to be resolved against the directory of
 * the page doing the linking, and stripped of the numeric ordering prefixes
 * that never appear in slugs.
 */
export function resolveDocHref(href: string, baseDir: string): string {
  const segments = baseDir ? baseDir.split('/') : [];

  for (const segment of href.replace(/\.md$/, '').split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') segments.pop();
    else segments.push(segment.replace(/^\d+[-_]/, ''));
  }

  return `/help/${segments.join('/')}`;
}

/**
 * Resolves a relative image src to the URL Vite emitted for that file.
 * Returns undefined when the image isn't part of the manual (an absolute URL,
 * or a path that doesn't exist), so the caller can fall back to the raw src.
 */
export function resolveImageSrc(src: string, baseDir: string): string | undefined {
  if (/^(https?:)?\/\//.test(src) || src.startsWith('data:')) return undefined;

  const segments = baseDir ? baseDir.split('/') : [];
  for (const segment of src.split('/')) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') segments.pop();
    else segments.push(segment);
  }

  return manualImages[segments.join('/')];
}

/**
 * Tailwind has no typography plugin in this project, so every element is styled
 * explicitly here rather than through a `prose` class.
 */
function buildComponents(baseDir: string): Components {
  const linkClass = 'text-blue-600 hover:text-blue-700 underline';

  return {
    h1: ({ children }) => (
      <h1 className="text-3xl font-bold text-gray-900 mb-6 scroll-mt-6">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2
        id={slugifyHeading(toText(children))}
        className="text-xl font-bold text-gray-900 mt-10 mb-3 pt-4 border-t border-gray-200 scroll-mt-6 first:mt-0 first:border-0 first:pt-0"
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        id={slugifyHeading(toText(children))}
        className="text-base font-bold text-gray-900 mt-6 mb-2 scroll-mt-6"
      >
        {children}
      </h3>
    ),
    p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-4">{children}</p>,
    ul: ({ children }) => (
      <ul className="list-disc pl-5 mb-4 space-y-1.5 text-sm text-gray-700">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-5 mb-4 space-y-1.5 text-sm text-gray-700">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
    // Callouts. The manual uses blockquotes for "watch out for this" asides.
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-amber-400 bg-amber-50 rounded-r-md px-4 py-3 mb-4 [&>p:last-child]:mb-0">
        {children}
      </blockquote>
    ),
    // Spacing only. The manual puts a `---` before most section headings, and
    // h2 already draws its own rule — a visible line here would double it up.
    hr: () => <hr className="my-6 border-0" />,
    // Spans, not <figure>: markdown puts images inside a paragraph, and a
    // block-level figure there is invalid nesting the browser would hoist out.
    img: ({ src, alt }) => {
      const resolved = typeof src === 'string' ? resolveImageSrc(src, baseDir) : undefined;
      return (
        <span className="block my-6">
          <img
            src={resolved ?? (typeof src === 'string' ? src : '')}
            alt={alt ?? ''}
            loading="lazy"
            className="w-full rounded-md border border-gray-200 shadow-sm"
          />
          {alt && <span className="block mt-2 text-xs text-gray-500">{alt}</span>}
        </span>
      );
    },
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4 rounded-md border border-gray-200">
        <table className="w-full text-sm text-left">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50 text-gray-600">{children}</thead>,
    th: ({ children }) => (
      <th className="px-4 py-2.5 font-medium border-b border-gray-200">{children}</th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2.5 text-gray-700 border-b border-gray-100 align-top">{children}</td>
    ),
    code: ({ children, className }) => {
      // Fenced blocks arrive with a language class; inline code never does.
      const isBlock = typeof className === 'string' && className.includes('language-');
      if (isBlock) return <code className="font-mono text-xs leading-relaxed">{children}</code>;
      return (
        <code className="font-mono text-[0.85em] bg-gray-100 text-gray-800 rounded px-1.5 py-0.5">
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="overflow-x-auto bg-gray-50 border border-gray-200 rounded-md p-4 mb-4 text-gray-700">
        {children}
      </pre>
    ),
    a: ({ href, children }) => {
      // Links between manual pages stay inside the SPA; anything else opens away.
      if (href?.endsWith('.md')) {
        return (
          <Link to={resolveDocHref(href, baseDir)} className={linkClass}>
            {children}
          </Link>
        );
      }
      if (href?.startsWith('/')) {
        return (
          <Link to={href} className={linkClass}>
            {children}
          </Link>
        );
      }
      return (
        <a href={href} target="_blank" rel="noreferrer" className={linkClass}>
          {children}
        </a>
      );
    },
  };
}

export function Markdown({ children, baseDir = '' }: { children: string; baseDir?: string }) {
  const components = useMemo(() => buildComponents(baseDir), [baseDir]);

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
}
